import { useEffect } from "react";
import { useSession } from "./auth";
import { LoginPage } from "./LoginPage";
import { AppShell } from "./AppShell";
import { DashboardPage } from "./DashboardPage";
import { CapabilityRegister } from "./CapabilityRegister";
import { CapabilityDetail } from "./CapabilityDetail";
import { ADRIndex } from "./ADRIndex";
import { ADRReader } from "./ADRReader";
import { RunbookIndex } from "./RunbookIndex";
import { RunbookReader } from "./RunbookReader";
import { DesignIndex } from "./DesignIndex";
import { DesignReader } from "./DesignReader";
import { BitsGallery } from "./BitsGallery";
import { BitReader } from "./BitReader";
import { SearchPage } from "./SearchPage";
import { ProgressPage } from "./ProgressPage";
import { CortexMark } from "./CortexMark";
import { navigate, parseRoute, useLocation } from "./router";
import { useRepos } from "./useRepos";
import { landingPath, LAST_REPO_STORAGE_KEY } from "./repoTypes";

// SECTION_NAMES is the closed list of pre-multi-repo route segments — used
// only to detect a bookmark/link minted before repo-prefixed URLs existed
// (e.g. "/adrs/ADR-0011"), so it can be redirected once rather than
// misparsed as if "adrs" were a repo key (parseRoute has no way to tell the
// two apart on its own; App.tsx is the one place that knows the difference).
const SECTION_NAMES = ["capabilities", "adrs", "runbooks", "design", "bits", "progress"];

// App gates the shell behind the real OFLIC session (M2): while the boot
// session check runs it shows a splash; unauthenticated users see the login
// screen; authenticated users see the routed app. The access boundary is
// enforced server-side (JWT middleware on /api + OFLIC's login-time gate).
// Once signed in, GET /api/repos is fetched before any routed page renders
// (same splash-gated discipline as the session check) — nav/repo-resolution
// correctness depends on knowing the real configured repo list, not a
// hardcoded guess.
export function App() {
  const { session, loading, beginLogin, logout } = useSession();
  const { pathname, search } = useLocation();
  const repos = useRepos(session?.token);

  const firstSegment = pathname.split("/")[1] ?? "";
  const needsLegacyRedirect = pathname === "/" || SECTION_NAMES.includes(firstSegment);
  const repoList = repos.repos?.repos ?? [];
  const defaultRepo = repos.repos?.defaultRepo;

  // Redirect once repos are known, rather than ever rendering a page
  // against the unprefixed path — every pre-existing bookmark/link still
  // resolves. The bare root remembers the last repo explicitly switched to
  // (see AppShell's repo selector); a legacy section bookmark always goes
  // to the configured default, since it predates any notion of "current
  // repo" and has no browsing history of its own to honour.
  useEffect(() => {
    if (!session || repos.status !== "ready" || !defaultRepo || !needsLegacyRedirect) return;
    if (pathname === "/") {
      const stored = window.localStorage.getItem(LAST_REPO_STORAGE_KEY);
      const target = repoList.find((r) => r.key === stored) ?? repoList.find((r) => r.key === defaultRepo) ?? repoList[0];
      if (target) navigate(landingPath(target), { replace: true });
    } else {
      navigate(`/${defaultRepo}${pathname}${search}`, { replace: true });
    }
  }, [session, repos.status, defaultRepo, repoList, needsLegacyRedirect, pathname, search]);

  if (loading) {
    return (
      <div className="splash" role="status" aria-live="polite">
        <CortexMark />
        <p>Checking your session…</p>
      </div>
    );
  }

  if (!session) {
    return <LoginPage onSignIn={beginLogin} />;
  }

  if (repos.status === "loading") {
    return (
      <div className="splash" role="status" aria-live="polite">
        <CortexMark />
        <p>Loading configured repositories…</p>
      </div>
    );
  }

  if (repos.status === "error") {
    // Never fall back to a hardcoded repo list — nav correctness is
    // load-bearing (a wrong/stale nav silently offers links that 404), so
    // this gets the same "show the backend's real message" treatment as
    // every reader's 503 state.
    return (
      <div className="splash" role="status" aria-live="polite">
        <CortexMark />
        <p className="err">{repos.message}</p>
      </div>
    );
  }

  if (needsLegacyRedirect) {
    // The effect above is about to replace this URL — render nothing
    // meaningful for the one frame before it fires rather than parsing the
    // unprefixed path as a bogus repo key.
    return (
      <div className="splash" role="status" aria-live="polite">
        <CortexMark />
      </div>
    );
  }

  const route = parseRoute(pathname);
  let page;
  if (route.section === "search") {
    page = <SearchPage token={session.token} />;
  } else if (!repoList.some((r) => r.key === route.repo)) {
    // Not one of the real configured repos — a typo, or a stale link to a
    // repo since removed from config. Never silently fall through to the
    // dashboard as if nothing were wrong.
    page = (
      <section className="panel">
        <h2>Unknown route</h2>
        <p>{`"${route.repo}" is not a recognised repository.`}</p>
      </section>
    );
  } else {
    switch (route.section) {
      case null:
        page = <DashboardPage token={session.token} />;
        break;
      case "capabilities":
        page = route.id ? <CapabilityDetail token={session.token} id={route.id} /> : <CapabilityRegister token={session.token} />;
        break;
      case "adrs":
        page = route.id ? <ADRReader token={session.token} id={route.id} /> : <ADRIndex token={session.token} />;
        break;
      case "runbooks":
        page = route.id ? <RunbookReader token={session.token} id={route.id} /> : <RunbookIndex token={session.token} />;
        break;
      case "design":
        page = route.id ? <DesignReader token={session.token} id={route.id} /> : <DesignIndex token={session.token} />;
        break;
      case "bits":
        page = route.id ? <BitReader token={session.token} id={route.id} /> : <BitsGallery token={session.token} />;
        break;
      case "progress":
        page = <ProgressPage token={session.token} />;
        break;
      default:
        page = (
          <section className="panel">
            <h2>Unknown route</h2>
            <p>{`"${route.section}" is not a recognised section.`}</p>
          </section>
        );
    }
  }

  return (
    <AppShell session={session} onSignOut={logout} repos={repoList} defaultRepo={defaultRepo ?? ""}>
      {page}
    </AppShell>
  );
}
